import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { LeadDto, LeadStatus } from '@mys/contracts';
import {
  BULK_TARGET_STATUSES,
  groupByStatus,
  LEAD_STATUSES,
  leadsExportHref,
  nextStatus,
  STATUS_LABELS,
  useLeads,
  useMoveLeads,
  useUpdateLead,
  waReplyHref,
} from '../lib/leads.js';
import { ApiError } from '../lib/api.js';
import { ReplyClock } from '../components/ReplyClock.js';
import { useSession } from '../lib/session.js';

/* ============================================================================
   /leads — the kanban.
   ----------------------------------------------------------------------------
   Seven columns on a desktop, wrapping; below 768px, one status-filtered list.
   Seven columns on a 360px Android is seven unreadable columns, and this
   screen is checked one-handed, standing in a shop.

   **Advancing a lead is still a BUTTON.** Drag-and-drop on a touch screen
   fights the scroll gesture, and "move this to the next column" is one tap
   either way. Slice 1.6b ADDED dragging without taking the button away, and
   only where a mouse exists: `(hover: hover) and (pointer: fine)`, the same
   query every `:hover` rule in this product sits inside. On a phone nothing
   below is reachable and nothing is draggable.

   Native HTML5 drag events, no library. dnd-kit is about 10 KB gzipped and
   this bundle has under 5 KB of its 120 KB budget left — but the better
   reason is that HTML5 drag does not work on touch at all, which here is the
   feature rather than the limitation.

   Dropping is restricted to the same five stages as the bulk bar, and the
   rule lives in `BULK_TARGET_STATUSES` with its reasoning: `lost` is asked
   for a reason in the same request, and `won` is what converting to a client
   does.
   ========================================================================== */

/** True where a real pointer exists. Read once rather than per render — this
    cannot change without a new device, and `matchMedia` in a render body is a
    layout read on every keystroke in the search box. */
const CAN_DRAG =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function LeadCard({
  lead,
  selected,
  onSelect,
}: {
  lead: LeadDto;
  selected: boolean;
  onSelect: (id: string, on: boolean) => void;
}) {
  const update = useUpdateLead(lead.id);
  const next = nextStatus(lead.status);

  return (
    <div
      className="lead-card"
      draggable={CAN_DRAG}
      onDragStart={(event) => {
        /* The id travels in the drag, not in a module variable. A variable
           survives a drag that was cancelled, abandoned or started in another
           tab; `dataTransfer` is cleared by the browser when the drag ends. */
        event.dataTransfer.setData('text/plain', lead.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
    >
      <div className="lead-card__top">
        {/* `draggable={false}` on BOTH links in this card, and it is not
            cosmetic. An `<a href>` is draggable by default in every browser,
            so grabbing the card by its name — or by the WhatsApp button
            below, which is the biggest target on it — starts a LINK drag
            rather than a card drag. Measured in Chromium: the dragstart fired
            on the WhatsApp anchor, the drop carried a different card's id,
            and the wrong lead moved. Turning it off makes the whole card the
            handle, which is what it looks like. */}
        <Link to={`/leads/${lead.id}`} className="lead-card__business" draggable={false}>
          {lead.business}
        </Link>
        <input
          type="checkbox"
          className="lead-card__pick"
          checked={selected}
          /* Named, because "checkbox" repeated nine times down a board tells a
             screen reader nothing about which lead is being selected. */
          aria-label={`Select ${lead.business}`}
          onChange={(event) => onSelect(lead.id, event.target.checked)}
        />
      </div>
      <div className="lead-card__meta">
        {lead.name ?? 'no name'} · {lead.plan ?? 'no plan'}
      </div>
      {/* The promise, on every card. It is the first thing to look at on this
          screen and the reason the screen is opened at all. */}
      <div className="lead-card__clock">
        <ReplyClock lead={lead} />
        {lead.assignedToName ? <span className="lead-card__who">{lead.assignedToName}</span> : null}
      </div>
      <div className="row" style={{ marginTop: 8, gap: 6 }}>
        <a
          className="btn btn--ghost btn--small"
          href={waReplyHref(lead)}
          target="_blank"
          rel="noreferrer"
          draggable={false}
        >
          WhatsApp
        </a>
        {next ? (
          <button
            type="button"
            className="btn btn--small"
            disabled={update.isPending}
            onClick={() => update.mutate({ status: next })}
          >
            → {STATUS_LABELS[next]}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function LeadsPage() {
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const move = useMoveLeads();

  /* Which cards are ticked, and which column a drag is currently over.
     A Set of ids rather than a flag on each lead: the leads come from the
     query cache and are replaced wholesale on every refetch, so anything
     stored ON them is lost the moment a move invalidates the board. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [over, setOver] = useState<LeadStatus | null>(null);
  const statusParam = params.get('status');
  const status = LEAD_STATUSES.includes(statusParam as LeadStatus)
    ? (statusParam as LeadStatus)
    : undefined;

  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';

  /* Both read off the URL rather than out of component state, so every view of
     this board is a link somebody can send — "the overdue ones" and "mine"
     included. `assignedTo=me` is resolved by the API against the session, so
     that link shows the recipient THEIR leads, which is the only thing it can
     honestly mean. */
  const overdue = params.get('overdue') === 'true';
  const mine = params.get('assignedTo') === 'me';

  const { data, isPending, error } = useLeads({
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
    ...(overdue ? { overdue } : {}),
    ...(mine ? { assignedTo: 'me' } : {}),
  });

  function applySearch(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('q', value);
    else next.delete('q');
    setParams(next, { replace: true });
  }

  function setStatus(value: LeadStatus | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set('status', value);
    else next.delete('status');
    setParams(next, { replace: true });
  }

  /** Toggle one flag in the URL, deleting it rather than writing `false`. */
  function toggleParam(key: string, value: string, on: boolean) {
    const next = new URLSearchParams(params);
    if (on) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const columns = groupByStatus(data?.items ?? []);
  const counts = data?.counts ?? {};
  const leads = data?.items ?? [];

  /* Resolved against what is ON SCREEN. A tick survives a filter change in
     the Set, and moving a lead that is no longer listed would be a move
     nobody can see happen. */
  const selected = leads.filter((lead) => picked.has(lead.id));

  function pick(id: string, on: boolean) {
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function moveTo(status: LeadStatus, chosen = selected) {
    if (chosen.length === 0) return;
    move.mutate(
      { leads: chosen.map((lead) => ({ id: lead.id, business: lead.business })), status },
      /* Cleared on settle, not on success: a partial run moved some of them,
         and leaving those ticked invites a second move of rows that already
         went. The message below says what happened. */
      { onSettled: () => setPicked(new Set()) },
    );
  }

  const droppable = (status: LeadStatus) => BULK_TARGET_STATUSES.includes(status);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Leads</h1>
          <p className="muted">{data ? `${data.total} shown` : 'Loading…'}</p>
        </div>

        <form
          className="row row--search"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch(search.trim());
          }}
        >
          <div className="field field--search">
            <label htmlFor="q" className="sr-only">
              Search by business, name or number
            </label>
            <input
              id="q"
              type="search"
              placeholder="Business, name or number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button className="btn btn--ghost btn--small" type="submit">
            Search
          </button>
        </form>
      </div>

      {/* Two filters that cut across the pipeline: what is late, and what is
          mine. Separate from the status row below because they combine with
          it — "my overdue new leads" is one URL. `aria-pressed` rather than a
          class alone: the only thing distinguishing an on button from an off
          one is its fill, which a screen reader does not see. */}
      <div className="row" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className={overdue ? 'btn btn--small' : 'btn btn--ghost btn--small'}
          aria-pressed={overdue}
          onClick={() => toggleParam('overdue', 'true', !overdue)}
        >
          Overdue
        </button>
        <button
          type="button"
          className={mine ? 'btn btn--small' : 'btn btn--ghost btn--small'}
          aria-pressed={mine}
          onClick={() => toggleParam('assignedTo', 'me', !mine)}
        >
          Mine
        </button>

        {/* Owner only, and the API refuses it for anyone else as well — this
            hands over every customer's name, number and message in one file.
            It carries whatever the board is filtered to, so "export the
            overdue ones" needs no second set of controls. */}
        {user?.role === 'owner' ? (
          <a
            className="btn btn--ghost btn--small"
            href={leadsExportHref({
              ...(status ? { status } : {}),
              ...(q ? { q } : {}),
              ...(overdue ? { overdue } : {}),
              ...(mine ? { assignedTo: 'me' } : {}),
            })}
          >
            Export CSV
          </a>
        ) : null}
      </div>

      {/* The status filter doubles as the mobile column switcher. */}
      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setStatus(undefined)}
        >
          All
        </button>
        {LEAD_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            onClick={() => setStatus(value)}
          >
            {STATUS_LABELS[value]} {counts[value] ?? 0}
          </button>
        ))}
      </div>

      {/* Only once something is ticked. A bar that is always there is chrome
          that is wrong six days out of seven. */}
      {selected.length > 0 ? (
        <div className="bulkbar" role="group" aria-label="Move the selected leads">
          <span>
            <strong>{selected.length}</strong> selected
          </span>
          {BULK_TARGET_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className="btn btn--ghost btn--small"
              disabled={move.isPending}
              onClick={() => moveTo(status)}
            >
              → {STATUS_LABELS[status]}
            </button>
          ))}
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => setPicked(new Set())}
          >
            Clear
          </button>
        </div>
      ) : null}

      {/* What the last bulk move actually did. "Moved 3. Patidar Kirana was
          not moved — …" rather than a count of failures nobody can act on. */}
      {move.data ? (
        <p className={move.data.failed ? 'alert' : 'muted'} role="status">
          Moved {move.data.moved}.
          {move.data.failed
            ? ` ${move.data.failed.business} was not moved — ${move.data.failed.message}`
            : ''}
        </p>
      ) : null}

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load leads.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      <div className="board">
        {/* With a status filter on, this renders exactly one column — which is
            what the mobile layout wants and what the filter means anyway. */}
        {(status ? [status] : LEAD_STATUSES).map((value) => (
          <section
            className={`board__col${over === value ? ' board__col--over' : ''}`}
            key={value}
            aria-labelledby={`col-${value}`}
            onDragOver={(event) => {
              /* preventDefault is what MAKES this a drop target — without it
                 the browser refuses the drop and the card springs back with
                 no explanation. Not calling it on `won` and `lost` is
                 therefore the whole implementation of "you cannot drop
                 there". */
              if (!droppable(value)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (over !== value) setOver(value);
            }}
            onDragLeave={() => setOver((current) => (current === value ? null : current))}
            onDrop={(event) => {
              if (!droppable(value)) return;
              event.preventDefault();
              setOver(null);
              const id = event.dataTransfer.getData('text/plain');
              const dropped = leads.find((lead) => lead.id === id);
              /* A card dropped on the column it came from is not a move, and
                 sending it would stop the reply clock for nothing — the API
                 counts a status change as a reply only when it CHANGES, but
                 the request would still be a lie about what happened. */
              if (dropped && dropped.status !== value) moveTo(value, [dropped]);
            }}
          >
            <h2 className="board__head" id={`col-${value}`}>
              <span>{STATUS_LABELS[value]}</span>
              <span className="pill">{counts[value] ?? 0}</span>
            </h2>

            {columns[value].length === 0 ? (
              <p className="muted" style={{ padding: '4px' }}>
                Nothing here yet
              </p>
            ) : (
              columns[value].map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  selected={picked.has(lead.id)}
                  onSelect={pick}
                />
              ))
            )}
          </section>
        ))}
      </div>
    </>
  );
}
